import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Check, FileSpreadsheet, Plus } from 'lucide-react';
import type { AgingReport, Obligation, ObligationList, ServiceContract, ServiceContractList, ServiceContractStatus } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { DataTable } from '../../components/DataTable';
import { Badge, EmptyState, PageHeader, SearchableSelect, TableSkeleton, cn } from '../../components/ui';
import { ContractForm } from './forms';
import { parseContractsSearch, type ContractsSearch } from './search';
import { BUCKET_TONE, ContractStatusBadge, Money, ObligationLine, clientName, fmtDate, useMoney } from './shared';

const STATUSES: ServiceContractStatus[] = ['draft', 'active', 'on_hold', 'completed', 'terminated'];

/**
 * The contracts register, the follow-ups due, and what the clients owe — three views of the same module. Every
 * figure comes from the server; the page only formats it (MRLCO added its columns up in the browser).
 */
export function ContractsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const search = parseContractsSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const [adding, setAdding] = useState(false);
  const canManage = can('service_contracts', 'can_edit');
  const setSearch = (patch: Partial<ContractsSearch>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    void navigate({ to: '/contracts', search: next as ContractsSearch });
  };

  const view = search.view ?? 'contracts';
  const views: Array<{ code: 'contracts' | 'followups' | 'receivables'; label: string }> = [
    { code: 'contracts', label: t('contracts.viewContracts') },
    { code: 'followups', label: t('contracts.viewFollowups') },
    { code: 'receivables', label: t('contracts.viewReceivables') },
  ];

  return (
    <div>
      <PageHeader
        title={t('contracts.pageTitle')}
        subtitle={t('contracts.pageSubtitle')}
        actions={canManage && <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> {t('contracts.newContract')}</button>}
      />
      <div className="mb-3 flex flex-wrap gap-1">
        {views.map((v) => (
          <button key={v.code} type="button" className={cn('btn-secondary', view === v.code && 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-200')} onClick={() => setSearch({ view: v.code === 'contracts' ? undefined : v.code, page: undefined })}>
            {v.label}
          </button>
        ))}
      </div>
      {view === 'contracts' && <ContractsList search={search} setSearch={setSearch} />}
      {view === 'followups' && <FollowupsView />}
      {view === 'receivables' && <ReceivablesView />}
      <ContractForm open={adding} onClose={() => setAdding(false)} onSaved={(c) => void navigate({ to: '/contracts/$id', params: { id: c.id }, search: {} })} />
    </div>
  );
}

function ContractsList({ search, setSearch }: { search: ContractsSearch; setSearch: (p: Partial<ContractsSearch>) => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const query = { q: search.q, status: search.status, client_entity_id: search.client, sort: search.sort, page: search.page ?? 1, page_size: 25 };
  const list = useQuery({ queryKey: ['contracts', 'list', query], queryFn: () => api.get<ServiceContractList>(`/api/contracts${qs(query)}`), placeholderData: (prev) => prev });
  const money = useMoney();

  const columns = useMemo<ColumnDef<ServiceContract, unknown>[]>(
    () => [
      { id: 'reference', accessorKey: 'reference', header: t('contracts.reference'), cell: ({ row }) => <span className="font-medium tabular-nums" dir="ltr">{row.original.reference}</span> },
      {
        id: 'title',
        header: t('contracts.contract'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate" dir="auto">{pick(i18n.language, row.original.title_en, row.original.title_ar)}</div>
            <div className="truncate text-xs text-neutral-500" dir="auto">{clientName(row.original, i18n.language)}</div>
          </div>
        ),
      },
      { id: 'status', accessorKey: 'status', header: t('contracts.status'), cell: ({ row }) => <ContractStatusBadge status={row.original.status} /> },
      {
        id: 'end_date',
        accessorKey: 'end_date',
        header: t('contracts.period'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-neutral-600 dark:text-neutral-300" dir="ltr">
            {fmtDate(row.original.start_date, i18n.language)} → {fmtDate(row.original.revised_end_date ?? row.original.end_date, i18n.language, t('contracts.openEnded'))}
          </span>
        ),
      },
      { id: 'value', header: t('contracts.figures.revisedValue'), enableSorting: false, cell: ({ row }) => <Money minor={row.original.figures.revised_value_minor} currency={row.original.currency} /> },
      {
        id: 'certified',
        header: t('contracts.figures.certified'),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Money minor={row.original.figures.certified_minor} currency={row.original.currency} />
            <span className="text-xs text-neutral-500 tabular-nums">{new Intl.NumberFormat(i18n.language).format(row.original.figures.percent_certified)}%</span>
          </span>
        ),
      },
      {
        id: 'outstanding',
        header: t('contracts.figures.outstanding'),
        enableSorting: false,
        cell: ({ row }) => (
          <span className={cn(row.original.figures.overdue_minor > 0 && 'text-rose-600 dark:text-rose-400')}>
            <Money minor={row.original.figures.outstanding_minor} currency={row.original.currency} />
            {row.original.figures.overdue_minor > 0 && <span className="ms-1 text-xs">({t('contracts.overdueShort')} {money(row.original.figures.overdue_minor)})</span>}
          </span>
        ),
      },
      {
        id: 'followup',
        header: t('contracts.nextFollowup'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.next_obligation ? (
            <span className="text-xs" dir="auto">
              {pick(i18n.language, row.original.next_obligation.title_en, row.original.next_obligation.title_ar)}
              <span className="block text-neutral-500" dir="ltr">{fmtDate(row.original.next_obligation.due_date, i18n.language)}</span>
            </span>
          ) : (
            <span className="text-xs text-neutral-400">—</span>
          ),
      },
    ],
    [i18n.language, t, money],
  );

  return (
    <DataTable
      columns={columns}
      data={list.data?.items ?? []}
      loading={list.isLoading}
      page={{ page: list.data?.meta.page ?? 1, page_size: list.data?.meta.page_size ?? 25, total: list.data?.meta.total ?? 0, onChange: (page) => setSearch({ page: page > 1 ? page : undefined }) }}
      sort={{ value: search.sort ?? '', onChange: (sort) => setSearch({ sort: sort || undefined, page: undefined }) }}
      emptyText={t('contracts.noContracts')}
      onRowClick={(c) => void navigate({ to: '/contracts/$id', params: { id: c.id }, search: {} })}
      toolbar={
        <div className="flex flex-wrap items-center gap-2">
          <input className="input w-48" placeholder={t('app.search')} value={search.q ?? ''} onChange={(e) => setSearch({ q: e.target.value || undefined, page: undefined })} />
          <div className="w-44"><SearchableSelect value={search.status ?? ''} onChange={(v) => setSearch({ status: (v as ServiceContractStatus) || undefined, page: undefined })} options={[{ value: '', label: t('contracts.allStatuses') }, ...STATUSES.map((s) => ({ value: s, label: t(`contracts.statuses.${s}`) }))]} /></div>
        </div>
      }
    />
  );
}

/** Every open follow-up the reader may see, overdue first: the part of the brief MRLCO had nothing for. */
function FollowupsView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['contracts', 'obligations', 'open'], queryFn: () => api.get<ObligationList>('/api/contracts/obligations?status=open') });
  const done = useMutation({
    mutationFn: (o: Obligation) => api.post(`/api/contracts/${o.contract.id}/obligations/${o.id}/close`, { status: 'done' }),
    onSuccess: () => { toast.success(t('contracts.obligationDone')); void qc.invalidateQueries({ queryKey: ['contracts'] }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  if (list.isLoading) return <TableSkeleton />;
  const items = list.data?.items ?? [];
  if (!items.length) return <EmptyState>{t('contracts.noFollowups')}</EmptyState>;
  const groups: Array<{ key: string; rows: Obligation[] }> = [
    { key: 'overdue', rows: items.filter((o) => (o.days_to_due ?? 0) < 0) },
    { key: 'soon', rows: items.filter((o) => (o.days_to_due ?? 0) >= 0 && (o.days_to_due ?? 0) <= 30) },
    { key: 'later', rows: items.filter((o) => (o.days_to_due ?? 0) > 30) },
  ];
  return (
    <div className="grid gap-4">
      {groups
        .filter((g) => g.rows.length)
        .map((g) => (
          <div key={g.key} className="card">
            <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-2 text-sm font-medium dark:border-neutral-800">
              {g.key === 'overdue' && <AlertTriangle size={14} className="text-rose-500" />}
              {t(`contracts.followupGroups.${g.key}`)}
              <Badge tone={g.key === 'overdue' ? 'danger' : 'neutral'}>{g.rows.length}</Badge>
            </div>
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {g.rows.map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-3 px-4 py-2">
                  <ObligationLine o={o} showContract />
                  {o.can_edit && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" disabled={done.isPending} onClick={() => done.mutate(o)}><Check size={12} /> {t('contracts.markDone')}</button>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      <p className="text-xs text-neutral-500">{t('contracts.followupsHint')}</p>
    </div>
  );
}

/** Receivables aging: every issued invoice still owed, by how late it is, per currency. */
function ReceivablesView() {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const report = useQuery({ queryKey: ['contracts', 'aging'], queryFn: () => api.get<AgingReport>('/api/contracts/aging') });
  const money = useMoney();
  if (report.isLoading) return <TableSkeleton />;
  const data = report.data;
  if (!data || !data.rows.length) return <EmptyState>{t('contracts.nothingOutstanding')}</EmptyState>;
  const buckets = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const;
  return (
    <div className="grid gap-4">
      <div className="card overflow-x-auto p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-neutral-500">
              <th className="p-2 text-start">{t('contracts.currency')}</th>
              {buckets.map((b) => <th key={b} className="p-2 text-end">{t(`contracts.buckets.${b}`)}</th>)}
              <th className="p-2 text-end">{t('contracts.figures.outstanding')}</th>
            </tr>
          </thead>
          <tbody>
            {data.totals.map((row) => (
              <tr key={row.currency} className="border-t border-neutral-200 dark:border-neutral-800">
                <td className="p-2 font-medium">{row.currency}</td>
                {buckets.map((b) => <td key={b} className="p-2 text-end tabular-nums" dir="ltr">{new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format((row.buckets[b] ?? 0) / 100)}</td>)}
                <td className="p-2 text-end font-semibold tabular-nums" dir="ltr">{new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format(row.outstanding_minor / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
          <span className="text-sm font-medium">{t('contracts.openInvoices')}</span>
          {can('service_contracts', 'can_view') && (
            <button type="button" className="btn-secondary" onClick={() => void api.download(`/api/contracts/aging/export${qs({ locale: i18n.language.startsWith('ar') ? 'ar' : 'en' })}`)}>
              <FileSpreadsheet size={16} /> {t('contracts.exportExcel')}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-neutral-500">
                <th className="p-2 text-start">{t('contracts.contract')}</th>
                <th className="p-2 text-start">{t('contracts.invoice')}</th>
                <th className="p-2 text-start">{t('contracts.dueDate')}</th>
                <th className="p-2 text-end">{t('contracts.figures.netDue')}</th>
                <th className="p-2 text-end">{t('contracts.figures.paid')}</th>
                <th className="p-2 text-end">{t('contracts.figures.outstanding')}</th>
                <th className="p-2 text-start">{t('contracts.aging')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.invoice.id} className="border-t border-neutral-200 dark:border-neutral-800">
                  <td className="p-2">
                    <Link to="/contracts/$id" params={{ id: r.contract.id }} search={{}} className="hover:underline">{r.contract.reference}</Link>
                    <span className="block truncate text-xs text-neutral-500" dir="auto">{r.contract.client.entity ? pick(i18n.language, r.contract.client.entity.name_en, r.contract.client.entity.name_ar) : r.contract.client.name}</span>
                  </td>
                  <td className="p-2">
                    <Link to="/contracts/$id/invoices/$invoiceId" params={{ id: r.contract.id, invoiceId: r.invoice.id }} className="tabular-nums hover:underline" dir="ltr">#{r.invoice.number}</Link>
                    <span className="block text-xs text-neutral-500">{t(`contracts.invoiceKinds.${r.invoice.kind}`)}</span>
                  </td>
                  <td className="p-2 text-xs" dir="ltr">{fmtDate(r.invoice.due_date, i18n.language)}</td>
                  <td className="p-2 text-end" dir="ltr">{money(r.net_due_minor, { sign: false })}</td>
                  <td className="p-2 text-end" dir="ltr">{money(r.paid_minor, { sign: false })}</td>
                  <td className="p-2 text-end font-medium" dir="ltr">{money(r.outstanding_minor, { sign: false })} {r.currency}</td>
                  <td className="p-2"><Badge tone={BUCKET_TONE[r.bucket]}>{t(`contracts.buckets.${r.bucket}`)}{r.days_overdue > 0 ? ` · ${r.days_overdue}` : ''}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-neutral-500">{t('contracts.agingHint', { date: fmtDate(data.as_of, i18n.language) })}</p>
    </div>
  );
}

export { parseContractsSearch };
